import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { Printer, ArrowLeft, Phone, Wifi, ShieldAlert, CheckCircle2, HelpCircle } from "lucide-react";

export const Route = createFileRoute("/_cashier/cashier-card")({
  component: CashierCardPage,
});

function CashierCardPage() {
  const { t } = useTranslation();
  const { current } = useCurrentLocation();
  const merchantId = current?.id ?? null;
  const [businessName, setBusinessName] = useState("");
  const [redemptionStyle, setRedemptionStyle] = useState("quick_burn");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!merchantId) return;
    supabase
      .from("merchants")
      .select("business_name, redemption_style")
      .eq("id", merchantId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setBusinessName(data.business_name);
          setRedemptionStyle(data.redemption_style);
        }
        setLoaded(true);
      });
  }, [merchantId]);

  const isVoucher = redemptionStyle === "verified_voucher";

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <p className="text-muted-foreground">{t("cashierCard.loading")}</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @page { size: A5; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-card { box-shadow: none !important; border: none !important; margin: 0 !important; }
        }
      `}</style>

      {/* Toolbar (hidden when printing) */}
      <div className="no-print bg-muted/40 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link
            to="/cashier"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> {t("cashierCard.backToCashier")}
          </Link>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
          >
            <Printer className="size-4" /> {t("cashierCard.printCard")}
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <p className="text-xs text-muted-foreground">{t("cashierCard.tip")}</p>
        </div>
      </div>

      {/* The card itself */}
      <div className="min-h-screen bg-muted/40 py-6 px-4 flex justify-center">
        <div
          className="print-card bg-background shadow-lg border border-border"
          style={{ width: "148mm", minHeight: "210mm", padding: "12mm" }}
        >
          {/* Header */}
          <div className="border-b-2 border-foreground pb-3 mb-4">
            <div className="flex items-baseline justify-between">
              <h1 className="text-2xl font-display font-bold tracking-tight">
                Tack. <span className="text-primary">{t("cashierCard.titleAccent")}</span>
              </h1>
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                {isVoucher ? t("cashierCard.voucherMode") : t("cashierCard.quickBurnMode")}
              </span>
            </div>
            {businessName && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("cashierCard.forStaffAt")} <span className="font-medium text-foreground">{businessName}</span>
              </p>
            )}
          </div>

          {/* Sections */}
          <div className="space-y-4 text-[11pt] leading-snug">
            {/* Primary flow — depends on redemption style */}
            <Section
              icon={<CheckCircle2 className="size-4 text-primary" />}
              title={isVoucher ? t("cashierCard.primaryTitleVoucher") : t("cashierCard.primaryTitleQuick")}
            >
              {isVoucher ? (
                <ol className="list-decimal list-inside space-y-1 text-foreground">
                  <li><Trans i18nKey="cashierCard.voucherStep1" components={[<span className="font-mono" />]} /></li>
                  <li>{t("cashierCard.voucherStep2")}</li>
                  <li><Trans i18nKey="cashierCard.voucherStep3" components={[<span className="font-semibold" />]} /></li>
                </ol>
              ) : (
                <ol className="list-decimal list-inside space-y-1 text-foreground">
                  <li><Trans i18nKey="cashierCard.quickStep1" components={[<span className="font-semibold" />]} /></li>
                  <li>{t("cashierCard.quickStep2")}</li>
                  <li><Trans i18nKey="cashierCard.quickStep3" components={[<span className="font-mono" />]} /></li>
                </ol>
              )}
            </Section>

            {/* Manual override */}
            <Section
              icon={<Phone className="size-4 text-primary" />}
              title={isVoucher ? t("cashierCard.manualTitleVoucher") : t("cashierCard.manualTitleQuick")}
            >
              <p className="text-foreground">
                <Trans i18nKey="cashierCard.manualIntro" components={[<span className="font-mono" />]} />
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-foreground mt-1">
                <li>{t("cashierCard.manualBullet1")}</li>
                <li><Trans i18nKey="cashierCard.manualBullet2" components={[<span className="italic" />]} /></li>
              </ul>
              <p className="text-foreground mt-1">
                <Trans i18nKey="cashierCard.manualOutro" components={[<span className="font-semibold" />]} />
              </p>
            </Section>

            {/* Red flags */}
            <Section
              icon={<ShieldAlert className="size-4 text-destructive" />}
              title={t("cashierCard.redFlagsTitle")}
            >
              <ul className="space-y-0.5 text-foreground">
                <li><Trans i18nKey="cashierCard.redFlag1" components={[<span className="font-mono" />, <span />, <span />, <span className="font-mono" />]} /></li>
                <li>{t("cashierCard.redFlag2")}</li>
                <li>{t("cashierCard.redFlag3")}</li>
              </ul>
            </Section>

            {/* Wi-Fi down */}
            <Section
              icon={<Wifi className="size-4 text-muted-foreground" />}
              title={t("cashierCard.wifiTitle")}
            >
              <p className="text-foreground">{t("cashierCard.wifiBody")}</p>
            </Section>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-3 border-t border-border flex items-center justify-between text-[9pt] text-muted-foreground">
            <span className="flex items-center gap-1">
              <HelpCircle className="size-3" />
              {t("cashierCard.footerHelp")} <span className="font-medium text-foreground">hello@tack.company</span>
            </span>
            <span className="font-mono">tack.company</span>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="flex items-center gap-1.5 font-semibold text-foreground mb-1 text-[11.5pt]">
        {icon} {title}
      </h2>
      <div className="pl-5.5 text-foreground/90">{children}</div>
    </div>
  );
}
